import React, { useState, useEffect, useMemo } from 'react';
import { 
  X, 
  Sliders, 
  Save, 
  ShieldCheck, 
  CheckCircle2, 
  Clock, 
  TrendingUp, 
  Info,
  AlertCircle
} from 'lucide-react';
import { Project, ManipulatedVariable } from '../types';

interface StepParametersModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentProject: Project | null;
  mvs: ManipulatedVariable[];
  onSaveParameters: (targetMoves: number, holdDuration: number) => Promise<void> | void;
}

const PRESET_MOVES = [10, 15, 20, 25, 30];
const PRESET_HOLD_MINUTES = [30, 45, 60, 90, 120];

export const StepParametersModal: React.FC<StepParametersModalProps> = ({
  isOpen,
  onClose,
  currentProject,
  mvs,
  onSaveParameters,
}) => {
  const currentTargetMoves = currentProject?.targetMovesPerMv ?? 20;
  const currentDefaultHold = currentProject?.defaultHoldDuration ?? 60;

  const [targetMoves, setTargetMoves] = useState<number>(currentTargetMoves);
  const [holdDuration, setHoldDuration] = useState<number>(currentDefaultHold);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      setTargetMoves(currentProject?.targetMovesPerMv ?? 20);
      setHoldDuration(currentProject?.defaultHoldDuration ?? 60);
      setIsSaving(false);
    }
  }, [isOpen, currentProject?.targetMovesPerMv, currentProject?.defaultHoldDuration]);

  // Calculate live preview metrics
  const preview = useMemo(() => {
    const totalMvs = mvs.length;
    const totalValidMoves = mvs.reduce((acc, m) => acc + (m.validMoves || 0), 0);

    // Current metrics
    const curTargetTotal = totalMvs * currentTargetMoves;
    const curProgressPct = curTargetTotal > 0 ? Math.min(100, Math.round((totalValidMoves / curTargetTotal) * 100)) : 0;
    const curReadyCount = mvs.filter((m) => m.validMoves >= currentTargetMoves).length;
    const curAdequateCount = mvs.filter((m) => m.validMoves >= Math.ceil(currentTargetMoves * 0.5) && m.validMoves < currentTargetMoves).length;

    // Projected metrics with new targetMoves
    const safeTarget = Math.max(1, targetMoves || 1);
    const newTargetTotal = totalMvs * safeTarget;
    const newProgressPct = newTargetTotal > 0 ? Math.min(100, Math.round((totalValidMoves / newTargetTotal) * 100)) : 0;
    const newReadyCount = mvs.filter((m) => m.validMoves >= safeTarget).length;
    const newAdequateCount = mvs.filter((m) => m.validMoves >= Math.ceil(safeTarget * 0.5) && m.validMoves < safeTarget).length;

    return {
      totalMvs,
      totalValidMoves,
      curTargetTotal,
      curProgressPct,
      curReadyCount,
      curAdequateCount,
      newTargetTotal,
      newProgressPct,
      newReadyCount,
      newAdequateCount,
      diffProgress: newProgressPct - curProgressPct,
      diffReady: newReadyCount - curReadyCount,
    };
  }, [mvs, currentTargetMoves, targetMoves]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (targetMoves < 1 || holdDuration < 1) return;

    setIsSaving(true);
    try {
      await onSaveParameters(targetMoves, holdDuration);
      onClose();
    } catch (err: any) {
      alert(`Failed to save parameters: ${err.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/50 backdrop-blur-xs overflow-y-auto">
      <div 
        className="bg-white border border-slate-200 rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden my-6 animate-in fade-in-50 zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-linear-to-r from-sky-50/70 via-white to-slate-50/60">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center shadow-xs">
              <Sliders className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <h2 className="font-bold text-slate-900 text-base">
                Step Test Parameters Configuration
              </h2>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {currentProject?.name || 'Active Project'} &bull; Target Moves & Hold Rules
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer disabled:opacity-50"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5">
          {/* Target Moves Per MV */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-sky-600" />
                <span>Target Valid Moves Per Variable</span>
              </label>
              <span className="text-xs font-mono font-bold text-sky-700 bg-sky-50 px-2 py-0.5 rounded border border-sky-200">
                {targetMoves} moves / MV
              </span>
            </div>

            <p className="text-[11px] text-slate-500 leading-relaxed">
              Defines the number of valid step perturbations required for each MV to reach 100% progress and 'Ready for Modeling' certification.
            </p>

            {/* Quick Preset Pills */}
            <div className="flex items-center gap-2 pt-1 flex-wrap">
              <span className="text-[10px] uppercase font-bold text-slate-400 mr-1">Presets:</span>
              {PRESET_MOVES.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setTargetMoves(preset)}
                  className={`px-3 py-1 text-xs font-semibold rounded-lg border transition-all cursor-pointer ${
                    targetMoves === preset
                      ? 'bg-sky-600 text-white border-sky-600 shadow-xs'
                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  {preset} Moves
                </button>
              ))}
            </div>

            {/* Custom Input */}
            <div className="pt-1">
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  max="100"
                  required
                  value={targetMoves}
                  onChange={(e) => setTargetMoves(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono font-bold text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400">
                  moves
                </span>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-100"></div>

          {/* Default Settling Hold Duration */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-amber-500" />
                <span>Default Settling Hold Duration</span>
              </label>
              <span className="text-xs font-mono font-bold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                {holdDuration} min ({Math.floor(holdDuration / 60)}h {holdDuration % 60}m)
              </span>
            </div>

            <p className="text-[11px] text-slate-500 leading-relaxed">
              Default hold time pre-populated in the Step Test recording dialog before steady-state evaluation is prompted.
            </p>

            {/* Quick Preset Pills */}
            <div className="flex items-center gap-2 pt-1 flex-wrap">
              <span className="text-[10px] uppercase font-bold text-slate-400 mr-1">Presets:</span>
              {PRESET_HOLD_MINUTES.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setHoldDuration(preset)}
                  className={`px-3 py-1 text-xs font-semibold rounded-lg border transition-all cursor-pointer ${
                    holdDuration === preset
                      ? 'bg-amber-600 text-white border-amber-600 shadow-xs'
                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  {preset}m
                </button>
              ))}
            </div>

            {/* Custom Input */}
            <div className="pt-1">
              <div className="relative">
                <input
                  type="number"
                  min="5"
                  max="480"
                  required
                  value={holdDuration}
                  onChange={(e) => setHoldDuration(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono font-bold text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400">
                  minutes
                </span>
              </div>
            </div>
          </div>

          {/* Live Impact Preview Card */}
          <div className="bg-slate-50/80 border border-slate-200 rounded-xl p-3.5 space-y-2.5">
            <div className="flex items-center justify-between text-xs font-bold text-slate-700">
              <span className="flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-sky-600" />
                <span>Live Impact Preview on Current Project</span>
              </span>
              <span className="text-[11px] font-mono text-slate-400">
                {preview.totalMvs} Variables &bull; {preview.totalValidMoves} Valid Moves
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-center text-xs">
              <div className="bg-white p-2 rounded-lg border border-slate-200">
                <div className="text-[10px] text-slate-400 uppercase font-semibold">Total Target</div>
                <div className="font-mono font-bold text-slate-800 text-sm mt-0.5">
                  {preview.newTargetTotal} moves
                </div>
                <div className="text-[10px] text-slate-400">was {preview.curTargetTotal}</div>
              </div>

              <div className="bg-white p-2 rounded-lg border border-slate-200">
                <div className="text-[10px] text-slate-400 uppercase font-semibold">Project Progress</div>
                <div className="font-mono font-bold text-sky-700 text-sm mt-0.5">
                  {preview.newProgressPct}%
                </div>
                <div className={`text-[10px] font-semibold ${
                  preview.diffProgress > 0 ? 'text-emerald-600' : preview.diffProgress < 0 ? 'text-rose-500' : 'text-slate-400'
                }`}>
                  {preview.diffProgress > 0 ? `+${preview.diffProgress}%` : preview.diffProgress < 0 ? `${preview.diffProgress}%` : 'Unchanged'}
                </div>
              </div>

              <div className="bg-white p-2 rounded-lg border border-slate-200 col-span-2 sm:col-span-1">
                <div className="text-[10px] text-slate-400 uppercase font-semibold">Model Ready MVs</div>
                <div className="font-mono font-bold text-emerald-700 text-sm mt-0.5">
                  {preview.newReadyCount} of {preview.totalMvs}
                </div>
                <div className="text-[10px] text-slate-400">
                  {preview.newAdequateCount} Adequate (≥{Math.ceil(targetMoves * 0.5)})
                </div>
              </div>
            </div>
          </div>

          {/* Strict Data Safety Guarantee Banner */}
          <div className="p-3 bg-emerald-50/70 border border-emerald-200 rounded-xl flex items-start gap-2.5 text-xs text-emerald-950">
            <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="leading-relaxed">
              <span className="font-bold text-emerald-900 block mb-0.5">
                Safe &amp; Non-Destructive Update
              </span>
              <span>
                Existing recorded steps, tallies (+/-/rejected moves), and variable data will <strong>NOT</strong> be modified, reset, or deleted. Only target denominators and progress percentages recalculate automatically based on this new target.
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2.5 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 rounded-lg text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-100 transition-colors cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-sky-600 hover:bg-sky-700 text-white shadow-sm transition-all cursor-pointer disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" />
              <span>{isSaving ? 'Applying...' : 'Apply & Save Parameters'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
